// WHICH news a given user should see, as pure functions. No DOM, no storage, no network — so every rule
// below is testable on its own, which matters because these rules decide whether a modal interrupts
// someone.
import { OTTO_VERSION } from "../version";
import { NEWS, SILENT_VERSIONS } from "./newsEntries";

// At most two releases and six slides in one sitting. A user returning after a long absence should not be
// handed a fifteen-slide wall on an interruption they did not ask for; the deck says plainly that older
// releases were left out rather than pretending they never happened.
export const MAX_VERSIONS = 2;
export const MAX_SLIDES = 6;

// Lazy on purpose. `core/ottoAccess` builds an axios client when it loads, and axios ships as ESM, which
// jest cannot parse — importing it at the top of this module (or of FilterAssistant) breaks every suite
// that renders the panel. Same lazy-require shape `core/telemetry` uses for the same reason.
const defaultCanUsePage = key => {
    try {
        // eslint-disable-next-line global-require
        const { canUseOttoPage } = require("../core/ottoAccess");
        return canUseOttoPage(key);
    } catch (e) {
        return false; // no access module ⇒ never advertise a gated page
    }
};

const parse = v =>
    String(v || "")
        .split(".")
        .map(n => parseInt(n, 10) || 0);

/** Semver compare, Otto-scoped (no pre-release tags — see version.js). Returns -1 / 0 / 1. */
export function compareVersions(a, b) {
    const x = parse(a);
    const y = parse(b);
    for (let i = 0; i < Math.max(x.length, y.length); i += 1) {
        const d = (x[i] || 0) - (y[i] || 0);
        if (d) return d > 0 ? 1 : -1;
    }
    return 0;
}

/**
 * The slides to show, newest release first, or [] for "say nothing".
 *
 * @param lastSeen        the version this user last dismissed, or null when they have no record
 * @param currentVersion  the OTTO_VERSION actually running (injected so tests need not fake the module)
 * @param entries         the registry (injected for the same reason)
 * @param canUsePage      (pageKey) => boolean — drops slides about pages this user cannot open
 * @param includeDrafts   dev/preview only; a draft entry never reaches a real user
 */
export function buildDeck({
    lastSeen = null,
    currentVersion = OTTO_VERSION,
    entries = NEWS,
    canUsePage = defaultCanUsePage,
    includeDrafts = false
} = {}) {
    const usable = (entries || [])
        .filter(e => e && Array.isArray(e.slides) && e.slides.length)
        .filter(e => includeDrafts || e.status === "published")
        // Never announce a release NEWER than the code actually running. The frontend lags the backend in
        // production by whole releases, and a deck describing 1.7.0 to someone running 1.6.0 is a support
        // ticket, not an announcement.
        .filter(e => compareVersions(e.version, currentVersion) <= 0)
        .sort((a, b) => compareVersions(b.version, a.version));

    if (!usable.length) return [];

    // No record at all — a brand-new user, or everyone on the release that first shipped this feature.
    // They get the CURRENT version only; replaying Otto's whole history at someone is not a welcome.
    const unseen = lastSeen
        ? usable.filter(e => compareVersions(e.version, lastSeen) > 0)
        : usable.slice(0, 1);

    const shown = unseen.slice(0, MAX_VERSIONS);
    const omitted = unseen.slice(MAX_VERSIONS);

    const slides = [];
    for (const entry of shown) {
        for (const slide of entry.slides) {
            if (slide.requires && !canUsePage(slide.requires)) continue;
            if (slides.length >= MAX_SLIDES) break;
            slides.push({
                ...slide,
                version: entry.version,
                date: entry.date,
                // Captions only make sense when the deck spans releases; a single-entry deck is already
                // titled with its version in the modal header.
                showVersion: shown.length > 1
            });
        }
    }

    if (slides.length && omitted.length) {
        const count = omitted.reduce((n, e) => n + e.slides.length, 0);
        slides.cappedNote = `${omitted.length} older release${omitted.length > 1 ? "s" : ""} (${count} slides) not shown.`;
    }
    return slides;
}

/** True when this user has news waiting — what the header's unread dot is driven by. */
export function hasUnseenNews(args) {
    return buildDeck(args).length > 0;
}

/**
 * Is the current version accounted for? Every release must either carry news or say it has none.
 *
 * Exported for `ottoNews.test.js` rather than used at runtime: an unaccounted-for version is a mistake to
 * catch in CI, not a condition to handle in the browser.
 */
export function versionIsAccountedFor(version = OTTO_VERSION, entries = NEWS, silent = SILENT_VERSIONS) {
    return (entries || []).some(e => e.version === version) || (silent || []).includes(version);
}
